/**
 * A person's own arrangement of the console sidebar, stored per org user in
 * `orgUserPreferences.sidebarLayout`.
 *
 * Storage constraints worth knowing before changing the shape:
 *
 * - The monolith's CamelCase parser and renderer rewrite every object key at every depth
 *   (camelCase -> snake_case on save, back on load). Plain camelCase keys survive that round trip;
 *   anything else (`my_item`, `ABC`) may not. So every field name here is plain camelCase, and
 *   identifiers -- route names, entry ids -- only ever appear as values, never as keys.
 * - The preferences blob is replaced whole on every save and has no server-side schema, so
 *   whatever comes back is untrusted: `normalizeLayout` checks it shape by shape and drops what it
 *   does not recognise rather than failing the whole sidebar.
 *
 * Every helper here is pure and returns a new layout, so callers can hand the result straight to
 * the save path without worrying about having mutated store state in place.
 */

export interface SidebarLink {
  kind: 'link'
  id: string
  /** A nav catalog entry id: a section hub id (e.g. 'attendance') or a route name. */
  target: string
  /** Overrides of the catalog defaults. Absent means "use the default", so improving a default reaches everyone who never changed it. */
  label?: string
  icon?: string
  color?: string
}

export interface SidebarSection {
  kind: 'section'
  id: string
  title: string
  collapsed?: boolean
  items: SidebarLink[]
}

export type SidebarEntry = SidebarLink | SidebarSection

export interface SidebarLayout {
  version: 1
  entries: SidebarEntry[]
}

export const SIDEBAR_LAYOUT_VERSION = 1

/** Generous for any real sidebar, small enough that a corrupt blob cannot bloat the org user entity. */
export const MAX_SIDEBAR_LINKS = 150
export const MAX_SIDEBAR_SECTIONS = 30
export const MAX_SIDEBAR_TEXT_LENGTH = 60

export const NEW_SECTION_TITLE = 'New section'

export function newEntryId (): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 10)
}

/**
 * The sidebar everyone gets until they change it -- the console's original fixed sidebar, less
 * Overview, which sits above every layout rather than in it.
 *
 * The ids are fixed rather than random so that the first save of an untouched layout (say,
 * collapsing a section) stores exactly what was on screen.
 */
export function defaultSidebarLayout (): SidebarLayout {
  const link = (target: string): SidebarLink => ({ kind: 'link', id: `default-${target}`, target })
  const section = (id: string, title: string, targets: string[]): SidebarSection => ({
    kind: 'section',
    id: `default-${id}`,
    title,
    items: targets.map(link)
  })

  return {
    version: SIDEBAR_LAYOUT_VERSION,
    entries: [
      section('operations', 'Operations', ['attendance', 'schedule', 'devices']),
      section('hr', 'HR Portal', ['users', 'timeoff', 'forms']),
      section('administration', 'Administration', ['costing', 'payroll', 'systemops', 'reports'])
    ]
  }
}

type ClassicLinkSpec = [target: string, label?: string, icon?: string]

/**
 * The Classic console's menu, rebuilt from catalog entries for people who would rather keep the
 * layout they know: Manage, Reports and Settings dropdowns, in Classic's order and with its names.
 * Dashboard is the fixed Overview item above every layout.
 *
 * A label or icon is given only where Classic's differs from the catalog default, so links still
 * follow any later improvement to a default. Entries the person cannot open are skipped at render
 * time like any other link, just as Classic hid them.
 */
export function classicSidebarLayout (): SidebarLayout {
  const link = ([target, label, icon]: ClassicLinkSpec): SidebarLink => ({
    kind: 'link',
    id: `classic-${target}`,
    target,
    ...(label ? { label } : {}),
    ...(icon ? { icon } : {})
  })
  const section = (id: string, title: string, specs: ClassicLinkSpec[], collapsed = false): SidebarSection => ({
    kind: 'section',
    id: `classic-${id}`,
    title,
    ...(collapsed ? { collapsed } : {}),
    items: specs.map(link)
  })

  return {
    version: SIDEBAR_LAYOUT_VERSION,
    entries: [
      section('manage', 'Manage', [
        ['discipline-infractions', 'Attendance'],
        ['clock-log-list'],
        ['forms'],
        ['geomap'],
        ['systemops', 'Messaging', 'Mail'],
        ['payroll'],
        ['punch-list'],
        ['schedule'],
        ['timecard-editor', 'Time Cards', 'Table'],
        ['timeoff', undefined, 'Plane']
      ]),
      section('reports', 'Reports', [
        ['absent', 'Absent'],
        ['absent-totals'],
        ['audit', 'Audit'],
        ['audit-time-entry', 'Audit Time'],
        ['briefings', 'Briefings'],
        ['clock-logs-report', 'Clock Logs'],
        ['credential-report', 'Credentials'],
        ['credential-roll-callreport'],
        ['job-transfer-report'],
        ['net-pay-analysis-report'],
        ['net-pay-annualization-report'],
        ['net-pay-month13-report'],
        ['payroll-report', 'Payroll'],
        ['pay-run-analysis-report'],
        ['pay-run-code-analysis-report'],
        ['pay-run-labor-analysis-report'],
        ['raw-punches-report', 'Raw Punches'],
        ['raw-punch-totals-report'],
        ['roll-call'],
        ['salary-report', 'Salary'],
        ['schedule-report', 'Schedule'],
        ['shiftcards-report'],
        ['tardy', 'Tardy'],
        ['timecards-report', 'Time Cards'],
        ['timeoff-report', 'Time Off'],
        ['time-off-balance-report'],
        ['time-off-monetization-report'],
        ['time-off-totals-report'],
        ['total-report', 'Totals'],
        ['memorized', 'Memorized Reports', 'Lightbulb']
      ], true),
      section('settings', 'Settings', [
        ['discipline-defaults', 'Attendance', 'ShieldAlert'],
        ['billing-subscription', 'Billing', 'Receipt'],
        ['costing', 'Costing'],
        ['devices'],
        ['integrations-apikeys', 'Integrations', 'Plug'],
        ['org-defaults', 'Organization', 'Building2'],
        ['payroll-defaults', 'Payroll', 'CreditCard'],
        ['entry-states', 'Punches', 'Fingerprint'],
        ['shift-defaults', 'Shifts', 'CalendarClock'],
        ['timeoff-defaults', 'Time Off', 'Plane'],
        ['users']
      ], true)
    ]
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const cleanText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined
  }
  const trimmed = value.trim().slice(0, MAX_SIDEBAR_TEXT_LENGTH)
  return trimmed || undefined
}

function normalizeLink (raw: unknown, seenIds: Set<string>): SidebarLink | null {
  if (!isRecord(raw) || raw.kind !== 'link') {
    return null
  }
  const id = cleanText(raw.id)
  const target = cleanText(raw.target)
  if (!id || !target || seenIds.has(id)) {
    return null
  }
  seenIds.add(id)

  const link: SidebarLink = { kind: 'link', id, target }
  const label = cleanText(raw.label)
  const icon = cleanText(raw.icon)
  const color = cleanText(raw.color)
  if (label) link.label = label
  if (icon) link.icon = icon
  if (color) link.color = color
  return link
}

/**
 * The stored layout, checked and cleaned, or null when nothing usable is stored -- in which case
 * the caller shows the default. Unknown icon or color names are kept as-is; they are resolved
 * (with a fallback) at render time, so a name added in a newer console still round-trips through
 * an older one.
 */
export function normalizeLayout (raw: unknown): SidebarLayout | null {
  if (!isRecord(raw) || raw.version !== SIDEBAR_LAYOUT_VERSION || !Array.isArray(raw.entries)) {
    return null
  }

  const seenIds = new Set<string>()
  let linkCount = 0
  let sectionCount = 0
  const entries: SidebarEntry[] = []

  for (const rawEntry of raw.entries) {
    if (isRecord(rawEntry) && rawEntry.kind === 'section') {
      const id = cleanText(rawEntry.id)
      if (!id || seenIds.has(id) || sectionCount >= MAX_SIDEBAR_SECTIONS) {
        continue
      }
      seenIds.add(id)
      sectionCount++

      const items: SidebarLink[] = []
      for (const rawItem of Array.isArray(rawEntry.items) ? rawEntry.items : []) {
        const link = linkCount < MAX_SIDEBAR_LINKS ? normalizeLink(rawItem, seenIds) : null
        if (link) {
          items.push(link)
          linkCount++
        }
      }

      const section: SidebarSection = {
        kind: 'section',
        id,
        title: cleanText(rawEntry.title) ?? NEW_SECTION_TITLE,
        items
      }
      if (rawEntry.collapsed === true) {
        section.collapsed = true
      }
      entries.push(section)
      continue
    }

    const link = linkCount < MAX_SIDEBAR_LINKS ? normalizeLink(rawEntry, seenIds) : null
    if (link) {
      entries.push(link)
      linkCount++
    }
  }

  return { version: SIDEBAR_LAYOUT_VERSION, entries }
}

/** Every link in the layout, top level and inside sections, in display order. */
export function allLinks (layout: SidebarLayout): SidebarLink[] {
  return layout.entries.flatMap(entry => entry.kind === 'section' ? entry.items : [entry])
}

function mapSections (layout: SidebarLayout, fn: (_section: SidebarSection) => SidebarSection): SidebarLayout {
  return {
    ...layout,
    entries: layout.entries.map(entry => entry.kind === 'section' ? fn(entry) : entry)
  }
}

/** Appends a link to the end of a section, or of the top level when `sectionId` is null. */
export function addLink (layout: SidebarLayout, sectionId: string | null, target: string): SidebarLayout {
  const link: SidebarLink = { kind: 'link', id: newEntryId(), target }
  if (sectionId === null) {
    return { ...layout, entries: [...layout.entries, link] }
  }
  return mapSections(layout, section =>
    section.id === sectionId ? { ...section, collapsed: false, items: [...section.items, link] } : section
  )
}

export function addSection (layout: SidebarLayout, title = NEW_SECTION_TITLE): { layout: SidebarLayout, sectionId: string } {
  const section: SidebarSection = { kind: 'section', id: newEntryId(), title, items: [] }
  return { layout: { ...layout, entries: [...layout.entries, section] }, sectionId: section.id }
}

export function renameSection (layout: SidebarLayout, sectionId: string, title: string): SidebarLayout {
  const cleaned = cleanText(title)
  if (!cleaned) {
    return layout
  }
  return mapSections(layout, section => section.id === sectionId ? { ...section, title: cleaned } : section)
}

export function setSectionCollapsed (layout: SidebarLayout, sectionId: string, collapsed: boolean): SidebarLayout {
  return mapSections(layout, section => {
    if (section.id !== sectionId) {
      return section
    }
    const { collapsed: _previous, ...rest } = section
    return collapsed ? { ...rest, collapsed: true } : rest
  })
}

export type SidebarLinkOverrides = Partial<Pick<SidebarLink, 'label' | 'icon' | 'color'>>

/**
 * Applies overrides to a link. An override that is empty, or equal to the catalog default, is
 * removed rather than stored, so the link goes back to following the default.
 */
export function updateLink (
  layout: SidebarLayout,
  linkId: string,
  overrides: SidebarLinkOverrides,
  defaults: Required<SidebarLinkOverrides>
): SidebarLayout {
  const apply = (link: SidebarLink): SidebarLink => {
    if (link.id !== linkId) {
      return link
    }
    const next: SidebarLink = { kind: 'link', id: link.id, target: link.target }
    for (const key of ['label', 'icon', 'color'] as const) {
      const value = cleanText(key in overrides ? overrides[key] : link[key])
      if (value && value !== defaults[key]) {
        next[key] = value
      }
    }
    return next
  }

  return {
    ...layout,
    entries: layout.entries.map(entry =>
      entry.kind === 'section' ? { ...entry, items: entry.items.map(apply) } : apply(entry)
    )
  }
}

export function removeLink (layout: SidebarLayout, linkId: string): SidebarLayout {
  return {
    ...layout,
    entries: layout.entries
      .filter(entry => entry.id !== linkId)
      .map(entry => entry.kind === 'section' ? { ...entry, items: entry.items.filter(item => item.id !== linkId) } : entry)
  }
}

/**
 * Removes a section. With `keepItems`, its links move to the top level where the section was, so
 * deleting a section never silently deletes the pages someone filed under it.
 */
export function removeSection (layout: SidebarLayout, sectionId: string, { keepItems }: { keepItems: boolean }): SidebarLayout {
  return {
    ...layout,
    entries: layout.entries.flatMap(entry => {
      if (entry.id !== sectionId || entry.kind !== 'section') {
        return [entry]
      }
      return keepItems ? entry.items : []
    })
  }
}

/** Replaces the top-level order, e.g. after a drag. */
export function setEntries (layout: SidebarLayout, entries: SidebarEntry[]): SidebarLayout {
  return { ...layout, entries }
}

/** Replaces one section's links, e.g. after a drag into, out of or within it. */
export function setSectionItems (layout: SidebarLayout, sectionId: string, items: SidebarLink[]): SidebarLayout {
  return mapSections(layout, section => section.id === sectionId ? { ...section, items } : section)
}
